import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
export const tageSeit = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
export const ERINNERUNG_TAGE = 14;

const useStatus = (pfad) => {
  const [d, setD] = useState(null);
  const laden = useCallback(() => axios.get(`${API}/erechnung-versand/${pfad}`).then((r) => setD(r.data)).catch(() => setD(null)), [pfad]);
  useEffect(() => { laden(); }, [laden]);
  return [d, laden];
};

const abfrageSenden = async (art, id, laden) => {
  if (!window.confirm("Abfrage-Mail jetzt wirklich senden?")) return;
  try {
    const r = await axios.post(`${API}/erechnung-versand/abfrage/${art}`, { ids: [id] });
    if (r.data.gesendet.length) toast.success(`Gesendet an ${r.data.gesendet[0].an}`);
    else toast.error(r.data.fehler[0]?.grund || "Nicht gesendet");
    laden();
  } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Versand"); }
};

export const AbfrageInfo = ({ am, anzahl, beantwortet }) => {
  if (!am) return <span>Noch nicht angefragt.</span>;
  const t = tageSeit(am);
  return (
    <span>
      {anzahl > 1 ? `${anzahl}× angefragt, zuletzt ` : "Angefragt "}{dt(am)}
      {!beantwortet && t >= ERINNERUNG_TAGE && <strong className="text-[#B7791F]"> · seit {t} Tagen keine Antwort – Erinnerung fällig</strong>}
    </span>
  );
};

export const RechnungEmailBox = ({ peId }) => {
  const [d, laden] = useStatus(`pflegeeinrichtung/${peId}`);
  const [email, setEmail] = useState("");
  useEffect(() => { setEmail(d?.rechnung_email || ""); }, [d]);
  if (!d) return null;
  const speichern = async () => {
    try {
      await axios.put(`${API}/pflegeeinrichtungen/${peId}/rechnung-email`, { rechnung_email: email });
      toast.success(email ? "Rechnungs-E-Mail gespeichert" : "Rechnungs-E-Mail entfernt");
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
  };
  return (
    <Card className="card-custom mb-5" data-testid="rechnung-email-box">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1"><Mail size={16} className="text-[#9A3B2E]" /><h3 className="font-semibold">Rechnungs-E-Mail (E-Rechnung)</h3></div>
        <p className="text-xs text-[#8E7B71] mb-3">Nur für Rechnungen. Rechnungen gehen als ZUGFeRD-E-Rechnung an diese Adresse – nur auf Knopfdruck.</p>
        <div className="flex flex-wrap gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="rechnung@…" className="max-w-sm" data-testid="rechnung-email-box-input" />
          <Button variant="outline" onClick={speichern} disabled={(d.rechnung_email || "") === email} data-testid="rechnung-email-box-save">Speichern</Button>
          <Button variant="outline" onClick={() => abfrageSenden("b2b", peId, laden)} data-testid="rechnung-email-box-abfrage">
            <Send size={14} className="mr-1.5" />{d.rechnung_email_abfrage_am ? "Erneut abfragen" : "Beim Kunden abfragen"}
          </Button>
        </div>
        <div className="text-[11px] text-[#8E7B71] mt-2 space-y-0.5" data-testid="rechnung-email-box-info">
          {d.rechnung_email && <div className="text-emerald-700">{d.rechnung_email_quelle} · {dt(d.rechnung_email_am)}</div>}
          <div><AbfrageInfo am={d.rechnung_email_abfrage_am} anzahl={d.rechnung_email_abfrage_anzahl} beantwortet={!!d.rechnung_email} />{d.rechnung_email_abfrage_an ? ` (an ${d.rechnung_email_abfrage_an})` : d.kontakt_email ? ` Abfrage ginge an ${d.kontakt_email}.` : " Keine Kontakt-E-Mail hinterlegt."}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const STATUS = {
  ja: { label: "Rechnungen per E-Mail erlaubt", icon: CheckCircle2, farbe: "#4F6650" },
  nein: { label: "Abgelehnt – Rechnungen per Post", icon: XCircle, farbe: "#C2452F" },
  offen: { label: "Keine Zustimmung – Rechnungen per Post", icon: Clock, farbe: "#B7791F" },
};

export const RechnungEinwilligungBox = ({ tnId }) => {
  const [d, laden] = useStatus(`teilnehmer/${tnId}`);
  if (!d) return null;
  const st = STATUS[d.email_rechnung_einwilligung] || STATUS.offen;
  const Icon = st.icon;
  const setzen = async (status) => {
    let beleg = "";
    if (status === "ja") {
      beleg = window.prompt("Wie liegt die Zustimmung vor? (z. B. „Anmeldeformular unterschrieben am …“)", "");
      if (beleg === null) return;
    } else if (!window.confirm("Zustimmung entfernen? Rechnungen müssen dann per Post raus.")) return;
    try {
      await axios.put(`${API}/teilnehmer/${tnId}/email-einwilligung`, { status, beleg });
      toast.success("Gespeichert");
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
  };
  return (
    <Card className="border-l-4 border-l-[#9A3B2E]" data-testid="rechnung-einwilligung-box">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2"><Mail size={16} className="text-[#9A3B2E]" /><h3 className="font-semibold">Rechnungen per E-Mail (Selbstzahler)</h3></div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="h-4 w-4 accent-[#9A3B2E]" checked={d.email_rechnung_einwilligung === "ja"} onChange={(e) => setzen(e.target.checked ? "ja" : "nein")} data-testid="rechnung-einwilligung-box-check" />
          <span className="text-sm font-semibold flex items-center gap-1" style={{ color: st.farbe }} data-testid="rechnung-einwilligung-box-status"><Icon size={14} /> {st.label}</span>
        </label>
        {d.email_rechnung_einwilligung && (
          <button className="text-[11px] text-[#8E7B71] underline mt-1" data-testid="rechnung-einwilligung-box-reset"
            onClick={async () => {
              if (!window.confirm("Eintrag auf „noch keine Angabe“ zurücksetzen?")) return;
              try { await axios.put(`${API}/teilnehmer/${tnId}/email-einwilligung`, { status: null }); toast.success("Zurückgesetzt"); laden(); }
              catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
            }}>auf „noch keine Angabe“ zurücksetzen</button>
        )}
        {d.email_rechnung_nachweis && (
          <p className="text-[11px] text-[#8E7B71] mt-2" data-testid="rechnung-einwilligung-box-nachweis">
            Nachweis: {[d.email_rechnung_nachweis.quelle, dt(d.email_rechnung_nachweis.zeitpunkt), d.email_rechnung_nachweis.beleg && `Beleg: ${d.email_rechnung_nachweis.beleg}`, d.email_rechnung_nachweis.erfasst_von && `erfasst von ${d.email_rechnung_nachweis.erfasst_von}`, d.email_rechnung_nachweis.ip && `IP ${d.email_rechnung_nachweis.ip}`].filter(Boolean).join(" · ")}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Button size="sm" variant="outline" onClick={() => abfrageSenden("selbstzahler", tnId, laden)} disabled={!d.email} data-testid="rechnung-einwilligung-box-abfrage">
            <Send size={14} className="mr-1.5" />{d.email_rechnung_abfrage_am ? "Erneut per Mail anfragen" : "Per Mail anfragen"}
          </Button>
          <span className="text-[11px] text-[#8E7B71]"><AbfrageInfo am={d.email_rechnung_abfrage_am} anzahl={d.email_rechnung_abfrage_anzahl} beantwortet={!!d.email_rechnung_einwilligung} />{!d.email && " Keine E-Mail hinterlegt."}</span>
        </div>
        {d.historie?.length > 1 && (
          <details className="mt-2 text-[11px] text-[#8E7B71]">
            <summary className="cursor-pointer">Verlauf ({d.historie.length})</summary>
            <ul className="mt-1 space-y-0.5">{d.historie.map((h, i) => <li key={i}>{dt(h.zeitpunkt)} · {h.status || "zurückgesetzt"} · {h.quelle}{h.erfasst_von ? ` (${h.erfasst_von})` : ""}</li>)}</ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
};
